import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNotify, useTranslate } from 'react-admin';
import {
  BbLevel,
  BbXpRule,
  bbDeleteLevel,
  bbGetLevels,
  bbGetXpRules,
  bbUpsertLevel,
  bbUpsertXpRule,
} from '../../../services/blindBoxService';
import { PermissionButton } from '../../../../../routes/components/permission-button';

type XpRuleMeta = {
  label: string;
  unit: string;
};

function getXpRuleMeta(rule: BbXpRule, t: (key: string) => string): XpRuleMeta {
  const m: Record<string, { labelKey: string; unitKey: string }> = {
    DAILY_LOGIN: {
      labelKey: 'module.blindBox.xpAction_daily_login',
      unitKey: 'module.blindBox.xpUnit_xp',
    },
    TRADE: {
      labelKey: 'module.blindBox.xpAction_trade',
      unitKey: 'module.blindBox.xpUnit_xp',
    },
    BOX_OPEN: {
      labelKey: 'module.blindBox.xpAction_box_open',
      unitKey: 'module.blindBox.xpUnit_xp',
    },
    INVITE: {
      labelKey: 'module.blindBox.xpAction_invite',
      unitKey: 'module.blindBox.xpUnit_xp_per_person',
    },
    SOCIAL_FOLLOW: {
      labelKey: 'module.blindBox.xpAction_social_follow',
      unitKey: 'module.blindBox.xpUnit_xp_per_item',
    },
    DAILY_PERP_TOP: {
      labelKey: 'module.blindBox.xpAction_leaderboard_top',
      unitKey: 'module.blindBox.xpUnit_xp_top_tier',
    },
  };

  const def = m[rule.action];
  return {
    label: def ? t(def.labelKey) : rule.description || rule.action,
    unit: def ? t(def.unitKey) : t('module.blindBox.xpUnit_xp'),
  };
}

export function SectionLevelsXp({ refreshKey }: { refreshKey: number }) {
  const t = useTranslate();
  const notify = useNotify();
  const [levels, setLevels] = useState<BbLevel[]>([]);
  const [xpRules, setXpRules] = useState<BbXpRule[]>([]);
  const [xpValues, setXpValues] = useState<Record<string, string>>({});
  const [lvlDlg, setLvlDlg] = useState<Partial<BbLevel> | null>(null);

  const load = useCallback(() => {
    Promise.all([bbGetLevels(), bbGetXpRules()]).then(([l, x]) => {
      setLevels(l);
      setXpRules(x);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  useEffect(() => {
    setXpValues((prev) => {
      const next = { ...prev };
      xpRules.forEach((r) => {
        if (next[r.action] === undefined) next[r.action] = String(r.xpAmount ?? 0);
      });
      return next;
    });
  }, [xpRules]);

  const saveLevel = async () => {
    if (!lvlDlg?.name) return;
    try {
      await bbUpsertLevel({
        id: lvlDlg.id,
        name: lvlDlg.name,
        requiredXp: lvlDlg.requiredXp!,
        lotteryWeight: lvlDlg.lotteryWeight!,
        displayOrder: lvlDlg.displayOrder!,
      });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      setLvlDlg(null);
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const saveXp = async (rule: BbXpRule, xp: number) => {
    try {
      await bbUpsertXpRule({ action: rule.action, xpAmount: xp });
      notify(t('module.blindBox.saveOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const deleteLevel = async (lv: BbLevel) => {
    if (!window.confirm(`Delete level #${lv.id} (${lv.name})?`)) return;
    try {
      await bbDeleteLevel(lv.id);
      notify(t('module.blindBox.deleteOk'), { type: 'success' });
      load();
    } catch (e: any) {
      notify(e?.message || 'Error', { type: 'error' });
    }
  };

  const xpOrder = useMemo(
    () => ['DAILY_LOGIN', 'TRADE', 'BOX_OPEN', 'INVITE', 'SOCIAL_FOLLOW', 'DAILY_PERP_TOP'],
    []
  );
  const xpRulesSorted = useMemo(() => {
    const order = new Map(xpOrder.map((k, i) => [k, i]));
    return [...xpRules].sort(
      (a, b) => (order.get(a.action) ?? 999) - (order.get(b.action) ?? 999)
    );
  }, [xpRules, xpOrder]);

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1">{t('module.blindBox.levels')}</Typography>
          <PermissionButton resource="bb-level-system" action="update">
            <Button
              startIcon={<AddIcon />}
              onClick={() =>
                setLvlDlg({
                  name: '',
                  requiredXp: 0,
                  lotteryWeight: 1,
                  displayOrder: levels.length + 1,
                })
              }
            >
              {t('module.blindBox.addLevel')}
            </Button>
          </PermissionButton>
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>id</TableCell>
                <TableCell>{t('module.blindBox.colName')}</TableCell>
                <TableCell>requiredXp</TableCell>
                <TableCell>lotteryWeight</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {levels.map((lv) => (
                <TableRow key={lv.id}>
                  <TableCell>{lv.id}</TableCell>
                  <TableCell>{lv.name}</TableCell>
                  <TableCell>{lv.requiredXp}</TableCell>
                  <TableCell>{lv.lotteryWeight}</TableCell>
                  <TableCell align="right">
                    <PermissionButton resource="bb-level-system" action="update">
                      <IconButton size="small" onClick={() => setLvlDlg(lv)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </PermissionButton>
                    <PermissionButton resource="bb-level-system" action="delete">
                      <IconButton size="small" color="error" onClick={() => deleteLevel(lv)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </PermissionButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/*<Box>*/}
      {/*  <Typography variant="subtitle1">{t('module.blindBox.xpRules')}</Typography>*/}
      {/*  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, mb: 2 }}>*/}
      {/*    {t('module.blindBox.xpRulesHint')}*/}
      {/*  </Typography>*/}

      {/*  <Box*/}
      {/*    sx={{*/}
      {/*      display: 'grid',*/}
      {/*      gap: 2,*/}
      {/*      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    {xpRulesSorted.map((r) => {*/}
      {/*      const meta = getXpRuleMeta(r, t);*/}
      {/*      const current = xpValues[r.action] ?? String(r.xpAmount ?? 0);*/}
      {/*      return (*/}
      {/*        <Box key={r.action} sx={{ minWidth: 0 }}>*/}
      {/*          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>*/}
      {/*            {meta.label}*/}
      {/*          </Typography>*/}
      {/*          <TextField*/}
      {/*            type="number"*/}
      {/*            size="small"*/}
      {/*            fullWidth*/}
      {/*            value={current}*/}
      {/*            onChange={(e) =>*/}
      {/*              setXpValues((p) => ({*/}
      {/*                ...p,*/}
      {/*                [r.action]: e.target.value,*/}
      {/*              }))*/}
      {/*            }*/}
      {/*            onBlur={() => {*/}
      {/*              const next = parseInt((xpValues[r.action] ?? current) || '0', 10) || 0;*/}
      {/*              if (next !== (r.xpAmount ?? 0)) saveXp(r, next);*/}
      {/*            }}*/}
      {/*          />*/}
      {/*          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>*/}
      {/*            {meta.unit}*/}
      {/*          </Typography>*/}
      {/*        </Box>*/}
      {/*      );*/}
      {/*    })}*/}
      {/*  </Box>*/}
      {/*</Box>*/}

      <Dialog open={!!lvlDlg} onClose={() => setLvlDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('module.blindBox.addLevel')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('module.blindBox.colName')}
              value={lvlDlg?.name || ''}
              onChange={(e) => setLvlDlg({ ...lvlDlg, name: e.target.value })}
              fullWidth
            />
            <TextField
              type="number"
              label="requiredXp"
              value={lvlDlg?.requiredXp ?? 0}
              onChange={(e) =>
                setLvlDlg({ ...lvlDlg, requiredXp: parseInt(e.target.value, 10) || 0 })
              }
              fullWidth
            />
            <TextField
              type="number"
              label="lotteryWeight"
              value={lvlDlg?.lotteryWeight ?? 0}
              onChange={(e) =>
                setLvlDlg({ ...lvlDlg, lotteryWeight: parseFloat(e.target.value) || 0 })
              }
              fullWidth
            />
            {/*<TextField*/}
            {/*  type="number"*/}
            {/*  label="displayOrder"*/}
            {/*  value={lvlDlg?.displayOrder ?? 0}*/}
            {/*  onChange={(e) =>*/}
            {/*    setLvlDlg({ ...lvlDlg, displayOrder: parseInt(e.target.value, 10) || 0 })*/}
            {/*  }*/}
            {/*  fullWidth*/}
            {/*/>*/}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLvlDlg(null)}>{t('actions.cancel')}</Button>
          <PermissionButton resource="bb-level-system" action="update">
            <Button variant="contained" onClick={saveLevel}>
              {t('actions.save')}
            </Button>
          </PermissionButton>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
